import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface TransactionDetails {
  user_id?: string;
  artisan_id?: string;
  debited_for: string;
  account_number: string;
}

export interface RequestTypes {
  transaction_id: string;
  transaction_details: TransactionDetails;
  debited_for: string;
  amount: string;
  created_at: string;
  prev_balance: string;
  new_balance: string;
}

export interface WithdrawalUser {
  _id: string;
  email: string;
  phone: string;
  country: string;
  currency_symbol: string;
  currency: string;
  first_name: string;
  last_name: string;
}

export interface WithdrawalRequest {
  _id: string;
  user: WithdrawalUser;
  user_type: "customer" | "artisan";
  amount: number;
  request_status: "pending" | "approved" | "rejected";
  transaction_id: string | null;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface WithdrawalResponse {
  success: boolean;
  message: string;
  payload: {
    data: {
      withdrawals: WithdrawalRequest[];
    };
  };
}

export interface WithdrawalApiResponse {
  success: boolean;
  message: string;
  payload: {
    user: {
      success: boolean;
      message: string;
      payload: {
        data: {
          withdrawals: WithdrawalRequest[];
        };
      };
    };
    artisan: {
      success: boolean;
      message: string;
      payload: {
        data: {
          withdrawals: WithdrawalRequest[];
        };
      };
    };
  };
}

export interface UserRequestResponse {
  payload: {
    data: {
      pending_transaction: RequestTypes[];
    };
  };
}

export interface ArtisanRequestResponse {
  payload: {
    data: {
      pending_transaction: RequestTypes[];
    };
  };
}

export interface RequestState {
  // Data
  userRequests: RequestTypes[];
  artisanRequests: RequestTypes[];
  withdrawalRequests: WithdrawalRequest[];

  // Loading states
  loadingUserRequests: boolean;
  loadingArtisanRequests: boolean;
  loadingWithdrawalRequests: boolean;
  processingRequest: boolean;
  validatingRequest: boolean;

  // Filters
  searchField: string;
  activeTab: string;
  selectedType: string;

  // Pagination
  currentPage: number;
  itemsPerPage: number;

  // UI state
  showApprovalModal: boolean;
  showDeclineModal: boolean;
  showWithdrawalModal: boolean;
  selectedRequest: WithdrawalRequest | null;

  // Error handling
  error: string | null;
  userRequestError: string | null;
  artisanRequestError: string | null;
  withdrawalRequestError: string | null;
  validationError: string | null;
}

const initialState: RequestState = {
  // Data
  userRequests: [],
  artisanRequests: [],
  withdrawalRequests: [],

  // Loading states
  loadingUserRequests: false,
  loadingArtisanRequests: false,
  loadingWithdrawalRequests: false,
  processingRequest: false,
  validatingRequest: false,

  // Filters
  searchField: "",
  activeTab: "All",
  selectedType: "Customer",

  // Pagination
  currentPage: 1,
  itemsPerPage: 10,

  // UI state
  showApprovalModal: false,
  showDeclineModal: false,
  showWithdrawalModal: false,
  selectedRequest: null,

  // Error handling
  error: null,
  userRequestError: null,
  artisanRequestError: null,
  withdrawalRequestError: null,
  validationError: null,
};

const requestSlice = createSlice({
  name: "request",
  initialState,
  reducers: {
    // Data actions
    setUserRequests: (state, action: PayloadAction<RequestTypes[]>) => {
      state.userRequests = action.payload;
    },
    setArtisanRequests: (state, action: PayloadAction<RequestTypes[]>) => {
      state.artisanRequests = action.payload;
    },
    setWithdrawalRequests: (
      state,
      action: PayloadAction<WithdrawalRequest[]>,
    ) => {
      state.withdrawalRequests = action.payload;
    },
    updateRequestStatus: (
      state,
      action: PayloadAction<{ id: string; status: string }>,
    ) => {
      const { id, status } = action.payload;
      const request = state.withdrawalRequests.find((req) => req._id === id);
      if (request) {
        request.request_status = status as "pending" | "approved" | "rejected";
      }
    },

    // Loading actions
    setLoadingUserRequests: (state, action: PayloadAction<boolean>) => {
      state.loadingUserRequests = action.payload;
    },
    setLoadingArtisanRequests: (state, action: PayloadAction<boolean>) => {
      state.loadingArtisanRequests = action.payload;
    },
    setLoadingWithdrawalRequests: (state, action: PayloadAction<boolean>) => {
      state.loadingWithdrawalRequests = action.payload;
    },
    setProcessingRequest: (state, action: PayloadAction<boolean>) => {
      state.processingRequest = action.payload;
    },
    setValidatingRequest: (state, action: PayloadAction<boolean>) => {
      state.validatingRequest = action.payload;
    },

    // Filter actions
    setSearchField: (state, action: PayloadAction<string>) => {
      state.searchField = action.payload;
    },
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },
    setSelectedType: (state, action: PayloadAction<string>) => {
      state.selectedType = action.payload;
    },

    // Pagination actions
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setItemsPerPage: (state, action: PayloadAction<number>) => {
      state.itemsPerPage = action.payload;
    },

    // UI actions
    setShowApprovalModal: (state, action: PayloadAction<boolean>) => {
      state.showApprovalModal = action.payload;
    },
    setShowDeclineModal: (state, action: PayloadAction<boolean>) => {
      state.showDeclineModal = action.payload;
    },
    setShowWithdrawalModal: (state, action: PayloadAction<boolean>) => {
      state.showWithdrawalModal = action.payload;
    },
    setSelectedRequest: (
      state,
      action: PayloadAction<WithdrawalRequest | null>,
    ) => {
      state.selectedRequest = action.payload;
    },

    // Error actions
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setUserRequestError: (state, action: PayloadAction<string | null>) => {
      state.userRequestError = action.payload;
    },
    setArtisanRequestError: (state, action: PayloadAction<string | null>) => {
      state.artisanRequestError = action.payload;
    },
    setWithdrawalRequestError: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.withdrawalRequestError = action.payload;
    },
    setValidationError: (state, action: PayloadAction<string | null>) => {
      state.validationError = action.payload;
    },
    clearErrors: (state) => {
      state.error = null;
      state.userRequestError = null;
      state.artisanRequestError = null;
      state.withdrawalRequestError = null;
      state.validationError = null;
    },

    // Reset actions
    resetRequests: (state) => {
      state.userRequests = [];
      state.artisanRequests = [];
      state.withdrawalRequests = [];
      state.loadingUserRequests = false;
      state.loadingArtisanRequests = false;
      state.loadingWithdrawalRequests = false;
      state.processingRequest = false;
      state.validatingRequest = false;
      state.searchField = "";
      state.activeTab = "All";
      state.selectedType = "All";
      state.currentPage = 1;
      state.itemsPerPage = 10;
      state.showApprovalModal = false;
      state.showDeclineModal = false;
      state.showWithdrawalModal = false;
      state.selectedRequest = null;
      state.error = null;
      state.userRequestError = null;
      state.artisanRequestError = null;
      state.withdrawalRequestError = null;
      state.validationError = null;
    },
  },
});

export const {
  // Data actions
  setUserRequests,
  setArtisanRequests,
  setWithdrawalRequests,
  updateRequestStatus,

  // Loading actions
  setLoadingUserRequests,
  setLoadingArtisanRequests,
  setLoadingWithdrawalRequests,
  setProcessingRequest,
  setValidatingRequest,

  // Filter actions
  setSearchField,
  setActiveTab,
  setSelectedType,

  // Pagination actions
  setCurrentPage,
  setItemsPerPage,

  // UI actions
  setShowApprovalModal,
  setShowDeclineModal,
  setShowWithdrawalModal,
  setSelectedRequest,

  // Error actions
  setError,
  setUserRequestError,
  setArtisanRequestError,
  setWithdrawalRequestError,
  setValidationError,
  clearErrors,

  // Reset actions
  resetRequests,
} = requestSlice.actions;

export default requestSlice.reducer;
